import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Divider, Flex, Form, message } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { FormInstance } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import {
  useTemplatesServiceCreateSystemTemplateEndpoint,
  useTemplatesServiceGetSystemTemplateEndpoint,
  useTemplatesServiceGetSystemTemplateListEndpointKey,
  useTreatmentServiceGetTreatmentPlanAdverseEffectEntryListEndpoint,
  useTreatmentServiceGetTreatmentPlanEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ContentType,
  GetConsultationResponse,
  GetPersonByIdResponse,
  Permissions,
  TreatmentPlanStatus
} from 'src/api/requests'
import { TreatmentTemplatesInfiniteSelect } from 'src/components'
import { PersonReactionsList } from 'src/features/persons/components/PersonTreatmentsSection'
import { EXISTING_TREATMENT_PLAN } from 'src/features/persons/utils/constants'
import { useUser } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler, toDayjs } from 'src/utils'

import { Recipes } from './Recipes'
import { useStyles } from './styles'
import { normalizeCron, Treatment, TreatmentTemplateModal } from './Treatments'

// Template dates are plain strings; invalid or past ones are left empty (the plan can't start in the past)
const toUsableDate = (value: unknown) => {
  const date = toDayjs(value)

  return date && !date.isBefore(dayjs(), 'day') ? date : undefined
}

type Props = {
  person?: GetPersonByIdResponse
  consultationData?: GetConsultationResponse
  form: FormInstance
}

export const PersonConsultationsTreatmentSection = ({
  person,
  consultationData,
  form
}: Props) => {
  const { t } = useLocales()
  const { hasPermission } = useUser()
  const { styles } = useStyles()
  const queryClient = useQueryClient()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const treatmentPlanId = form?.getFieldValue(EXISTING_TREATMENT_PLAN)
  const treatmentTemplateId = Form.useWatch('treatmentTemplate', form)

  const { data, isFetched } = useTreatmentServiceGetTreatmentPlanEndpoint(
    {
      id: treatmentPlanId
    },
    [treatmentPlanId],
    { enabled: !!treatmentPlanId }
  )

  const { data: treatmentTemplateData } =
    useTemplatesServiceGetSystemTemplateEndpoint(
      {
        id: String(treatmentTemplateId)
      },
      [String(treatmentTemplateId)],
      {
        enabled: !!treatmentTemplateId
      }
    )

  const { data: adverseReactions } =
    useTreatmentServiceGetTreatmentPlanAdverseEffectEntryListEndpoint(
      {
        id: String(treatmentPlanId)
      },
      [String(treatmentPlanId)],
      {
        enabled: !!treatmentPlanId
      }
    )

  const { mutate: createTemplate } =
    useTemplatesServiceCreateSystemTemplateEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useTemplatesServiceGetSystemTemplateListEndpointKey]
        })
        message.success(t('counterparts.template_created'))
        form.resetFields(['template_name', 'template_description'])
        setIsModalOpen(false)
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const isCancelled = data?.status === TreatmentPlanStatus.CANCELLED

  const onOpenModal = async () => {
    await form.validateFields([
      'simeCode',
      'treatment_start_date',
      'treatment_end_date',
      'treatment_type',
      'treatmentTypeRegimenId',
      'medicine'
    ])

    setIsModalOpen(true)
  }

  const onCreateTemplate = async () => {
    await form.validateFields(['template_name', 'template_description'])

    const values = await form.getFieldsValue()

    // Patient codes are person-specific, so they are not stored in templates
    const payload = {
      treatment_start_date: toDayjs(values?.treatment_start_date)?.format(
        yearMonthDaySlash
      ),
      treatment_end_date: toDayjs(values?.treatment_end_date)?.format(
        yearMonthDaySlash
      ),
      medicine: values?.medicine?.map((item: AnyObject) => ({
        internationalNameId: item?.internationalNameId,
        productFormId: {
          name: item?.productFormId?.name,
          id: item?.productFormId?.id
        },
        name: item?.name,
        amount: item?.amount,
        cron: normalizeCron(item?.cron),
        startDate: toDayjs(item?.startDate)?.format(yearMonthDaySlash),
        endDate: toDayjs(item?.endDate)?.format(yearMonthDaySlash),
        durationInDays: item?.durationInDays,
        amountToAdminister: item?.amountToAdminister,
        totalAdministered: item?.totalAdministered
      }))
    }

    createTemplate({
      requestBody: {
        name: values?.template_name,
        description: values?.template_description,
        isPublic: true,
        type: ContentType.CONSULTATION,
        content: payload
      }
    })
  }

  useEffect(() => {
    if (isFetched && data && !isCancelled) {
      form?.setFieldsValue({
        simeCode: person?.simeCode,
        tarvCode: person?.tarvCode,
        treatment_start_date: toDayjs(data?.created),
        treatment_end_date: toDayjs(data?.endDate),
        treatment_type: data?.treatmentType?.id,
        status: data?.status,
        treatmentTypeRegimenId: data?.treatmentTypeRegimen?.id,
        medicine: data?.prescribedTreatments?.map((item) => ({
          id: item?.id,
          name: item?.internationalName?.name,
          internationalNameId: item?.internationalName?.id,
          productFormId: {
            name: item?.productForm?.name,
            id: item?.productForm?.id
          },
          amount: item?.amount,
          cron: item?.scheduleExpression,
          startDate: toDayjs(item?.startDate),
          endDate: toDayjs(item?.endDate),
          durationInDays: item?.durationInDays,
          amountToAdminister: item?.amountToAdminister,
          totalAdministered: item?.totalAdministered
        }))
      })
    }
  }, [data, isCancelled, isFetched, person])

  useEffect(() => {
    if (adverseReactions) {
      form.setFieldValue('events', adverseReactions?.data)
    }
  }, [adverseReactions])

  useEffect(() => {
    if (consultationData) {
      form.setFieldValue(
        EXISTING_TREATMENT_PLAN,
        consultationData?.treatmentPlanId
      )
    }
  }, [consultationData])

  useEffect(() => {
    if (treatmentTemplateData) {
      // Only treatment fields are applied: consultation drafts share the template type and
      // carry other sections (string dates, another patient's codes) that must not leak in
      const content = treatmentTemplateData?.content ?? {}

      const medicine = Array.isArray(content?.medicine)
        ? content.medicine.map((item: AnyObject) => ({
            internationalNameId: item?.internationalNameId,
            productFormId: item?.productFormId,
            name: item?.name,
            amount: item?.amount,
            cron: normalizeCron(item?.cron),
            startDate: toUsableDate(item?.startDate),
            endDate: toUsableDate(item?.endDate),
            durationInDays: item?.durationInDays,
            amountToAdminister: item?.amountToAdminister,
            totalAdministered: item?.totalAdministered
          }))
        : undefined

      form.setFieldsValue({
        treatment_type: content?.treatment_type,
        treatmentTypeRegimenId: content?.treatmentTypeRegimenId,
        preventionTreatmentContingentTypeId:
          content?.preventionTreatmentContingentTypeId,
        isUrgent: content?.isUrgent,
        treatment_start_date: toUsableDate(content?.treatment_start_date),
        treatment_end_date: toUsableDate(content?.treatment_end_date),
        medicine
      })

      form
        .validateFields([
          'treatment_start_date',
          'treatment_end_date',
          'medicine',
          ...(medicine ?? []).map((_: AnyObject, i: number) => [
            'medicine',
            i,
            'cron'
          ])
        ])
        .catch(() => {})
    }
  }, [treatmentTemplateData])

  return (
    <Card>
      <Card
        title={t('consultations.treatment')}
        className={styles.treatmentCard}
      >
        <Flex vertical>
          {!data && (
            <>
              <Flex
                className={styles.treatmentContent}
                align="center"
                justify="space-between"
              >
                <Form.Item
                  name="treatmentTemplate"
                  label={t('templates.treatment_templates')}
                >
                  <TreatmentTemplatesInfiniteSelect />
                </Form.Item>

                <Button type="primary" onClick={onOpenModal}>
                  {t('counterparts.add_new_template_contract')}
                </Button>
              </Flex>

              <Divider />
            </>
          )}

          <Treatment form={form} person={person} />
          <Divider />

          {hasPermission(
            Permissions.TREATMENT_TREATMENT_PLAN_RECIPE_VIEW_ALL
          ) && (
            <>
              {treatmentPlanId && <Recipes form={form} />}
              <Divider />
            </>
          )}

          {!!adverseReactions?.data?.length && (
            <PersonReactionsList
              data={adverseReactions?.data}
              treatmentPlanId={treatmentPlanId}
            />
          )}
        </Flex>
      </Card>

      {isModalOpen && (
        <TreatmentTemplateModal
          onCancel={() => setIsModalOpen(false)}
          onOk={onCreateTemplate}
        />
      )}
    </Card>
  )
}
